import {
  Button,
  TextInput,
  Checkbox,
  Flex,
  Loader,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { updateNotification } from "../../utils/useNotification";
import { validateRequired } from "../../utils/validations";
import {
  getCompanyById,
  getCompanyPhoto,
  updateCompanyById,
  uploadCompanyPhoto,
} from "../../api/companyApi";
import PhotoUploadInput from "../common/PhotoUploadInput";
import Container from "../common/Container";
import { ActionIcon } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import AreYouSureModal from "../common/AreYouSureModal";
import { COMPANY_MODULES, CompanyModule } from "../../constants/companyModules";

const CompaniesUpdateForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);

  const [photos, setPhotos] = useState<{ [key: string]: File | null }>({
    hr: null, mobile: null, terminal: null,
  });
  const [previews, setPreviews] = useState<{ [key: string]: string | null }>({
    hr: null, mobile: null, terminal: null,
  });
  const [overlayVisible, setOverlayVisible] = useState<{ [key: string]: boolean }>({
    hr: false, mobile: false, terminal: false,
  });
  const [loadingState, setLoadingState] = useState<{ [key: string]: boolean }>({
    hr: false, mobile: false, terminal: false,
  });
  const [modules, setModules] = useState<{ [key: string]: boolean }>({
    isCalendarAvailable: false,
    isDocumentUploadAvailable: false,
    isApplicantAvailable: false,
    isBleAvailable: false,
    isGuestAccessAvailable: false,
  });

  const imageTypes = [
    { type: "hr", label: "Şirket Logosu Yükle" },
    { type: "mobile", label: "Mobil Logo Yükle" },
    { type: "terminal", label: "Terminal Logo Yükle" },
  ];

  const companyForm = useForm({
    initialValues: {
      name: "",
      code: "",
      address: "",
      taxNumber: "",
      note: "",
      taxOffice: "",
    },
    validate: {
      name: validateRequired,
      taxNumber: validateRequired,
      code: validateRequired,
      address: validateRequired,
      taxOffice: validateRequired,
    },
  });

  // Modül kapatmak geri alınamayan yan etkiler doğurabiliyor (ör. BLE kapanınca
  // şirket konumu siliniyor), o yüzden kapatma onay ister. Açmak serbest.
  const [moduleToDisable, setModuleToDisable] = useState<CompanyModule | null>(
    null
  );

  const setModule = (key: string, value: boolean) => {
    setModules((prev) => ({ ...prev, [key]: value }));
  };

  const toggleModule = (mod: CompanyModule) => {
    const isOn = modules[mod.key] ?? false;
    if (isOn) {
      setModuleToDisable(mod);
      return;
    }
    setModule(mod.key, true);
  };

  useEffect(() => {
    const fetchDetail = async () => {
      if (!id) return;
      try {
        const { data }: any = await getCompanyById(id);
        const companyData = data?.company || data?.data;

        if (companyData) {
          companyForm.setValues({
            name: companyData.name || "",
            code: companyData.code || "",
            address: companyData.address || "",
            taxNumber: companyData.taxNumber || "",
            note: companyData.note || "",
            taxOffice: companyData.taxOffice || "",
          });

          setModules({
            isCalendarAvailable: companyData.modules?.isCalendarAvailable || false,
            isApplicantAvailable: companyData.modules?.isApplicantAvailable || false,
            isDocumentUploadAvailable: companyData.modules?.isDocumentUploadAvailable || false,
            isBleAvailable: companyData.modules?.isBleAvailable || false,
            isGuestAccessAvailable: companyData.modules?.isGuestAccessAvailable || false,
          });

          for (const { type } of imageTypes) {
            try {
              const res: any = await getCompanyPhoto(id, type);
              if (res?.data && res.status === 200) {
                const imageURL = URL.createObjectURL(res.data);
                setPreviews((prev) => ({ ...prev, [type]: imageURL }));
              }
            } catch (e) { console.warn(`${type} logo bulunamadı.`); }
          }
        }
      } catch (error) {
        console.error("Detay getirme hatası:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [id]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setPhotos((prev) => ({ ...prev, [type]: file }));
      setPreviews((prev) => ({ ...prev, [type]: URL.createObjectURL(file) }));
    }
  };

  const uploadAllImages = async (companyId: string) => {
    for (const type of Object.keys(photos)) {
      if (!photos[type]) continue;
      const formData = new FormData();
      formData.append("companyId", companyId);
      formData.append("file", photos[type]!);
      try { await uploadCompanyPhoto(formData, type); } catch (e) { console.error(e); }
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        companyId: id,
        updates: { ...values, modules },
      };
      const res: any = await updateCompanyById(payload);
      
      if (res?.status === 200 || res?.data?.success) {
        updateNotification(res.data?.success || "Şirket başarıyla güncellendi");
        await uploadAllImages(id!);
        navigate("/companies");
      }
    } catch (error: any) {

      console.error("Hata:", error);
    }
  };
  if (loading) return <Center h="100vh"><Loader size="xl" color="#5c26ff" /></Center>;

  return (
    <Container>
      <ActionIcon
        variant="subtle"
        color="#5c26ff"
        size={44}
        onClick={() => navigate("/companies")}
      >
        <IconArrowLeft size={28} />
      </ActionIcon>
      <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <span style={{ fontSize: "24px", fontWeight: 600, color: "black" }}>
          Şirket Bilgileri Güncelleme
        </span>
      </div>

      <form
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          
        }}
        onSubmit={companyForm.onSubmit(handleSubmit)}
      >
        <Flex direction="column">
          <TextInput
            label="Şirket Adı"
            {...companyForm.getInputProps("name")}
          />
          <TextInput
            label="Şirket Kodu"
            {...companyForm.getInputProps("code")}
            value={companyForm.values.code.toUpperCase().replace(/\s/g, "")}
            onChange={(e) => {
              companyForm.setFieldValue("code", e.currentTarget.value.toUpperCase().replace(/\s/g, ""));
            }}
          />
          <TextInput
            label="Şirket Adresi"
            {...companyForm.getInputProps("address")}
          />
          <TextInput
            label="Vergi Numarası"
            {...companyForm.getInputProps("taxNumber")}
          />
          <TextInput
            label="Vergi Dairesi"
            {...companyForm.getInputProps("taxOffice")}
          />
          <TextInput
            label="Şirket Notu"
            {...companyForm.getInputProps("note")}
            withAsterisk={false}
          />
        </Flex>

        <Flex justify="space-between" gap={40} wrap="wrap">
          {imageTypes.map(({ type, label }) => (
            <PhotoUploadInput
              key={type}
              type={type}
              label={label}
              photo={photos[type]}
              preview={previews[type]}
              overlayVisible={overlayVisible[type]}
              loading={loadingState[type]}
              onFileChange={handleFileChange}
              setOverlayVisible={(visible) =>
                setOverlayVisible((prev) => ({ ...prev, [type]: visible }))
              }
            />
          ))}
        </Flex>

        <div style={{ textAlign: "center", marginBottom: "24px" }}>
        <span style={{ fontSize: "24px", fontWeight: 600, color: "black" }}>
          Modüller
        </span>
        </div>

        <Flex mt={15} direction="column" gap={15} maw={360}>
          {COMPANY_MODULES.map((mod) => (
            <Checkbox
              key={mod.key}
              color="#5c26ff"
              labelPosition="left"
              label={mod.label}
              checked={modules[mod.key] ?? false}
              onChange={() => toggleModule(mod)}
              styles={{ body: { justifyContent: "space-between", width: "100%" } }}
            />
          ))}
        </Flex>

        <Flex justify="space-between">
          <Button variant="default" onClick={() => navigate("/companies")}>
            Vazgeç
          </Button>
          <Button color="#5c26ff" type="submit">
            Güncelle
          </Button>
        </Flex>
      </form>

      <AreYouSureModal
        opened={Boolean(moduleToDisable)}
        setOpened={(open: boolean) => !open && setModuleToDisable(null)}
        proccessFunc={() => {
          if (moduleToDisable) setModule(moduleToDisable.key, false);
          setModuleToDisable(null);
        }}
        title={`"${moduleToDisable?.label}" modülünü kapat`}
        text={
          moduleToDisable?.offConsequence
            ? `${moduleToDisable.offConsequence} Devam etmek istiyor musunuz?`
            : "Bu modül şirketin panellerinde artık görünmeyecek. Devam etmek istiyor musunuz?"
        }
        confirmButtonText="Kapat"
        cancelButtonText="Vazgeç"
      />
    </Container>
  );
};

export default CompaniesUpdateForm;